// MultiCombo picks MANY items from a long list, with a search box.
//
// A <select multiple> makes you ctrl-click through dozens of rows and shows nothing
// about what you already chose. This filters as you type, adds each pick as a chip you
// can drop with one ✕, and hides from the list whatever is already chosen.
//
// Each chip carries its own hidden input, so the form posts the whole set under one
// name (material_id) and the server can replace the association wholesale — removing a
// chip really removes the link.
//
// Island Architecture: a light-DOM web component; the hidden inputs it manages are what
// the surrounding <form> submits.

export class MultiCombo extends HTMLElement {
  private input!: HTMLInputElement;
  private chips!: HTMLElement;
  private list!: HTMLElement;
  private options: HTMLElement[] = [];
  private activeIndex = -1;
  /** The field name each chip's hidden input carries. */
  private fieldName = "material_id";

  connectedCallback(): void {
    const input = this.querySelector<HTMLInputElement>("[data-multicombo-input]");
    const chips = this.querySelector<HTMLElement>("[data-multicombo-chips]");
    const list = this.querySelector<HTMLElement>("[data-multicombo-list]");
    if (!input || !chips || !list) return;

    this.input = input;
    this.chips = chips;
    this.list = list;
    this.options = Array.from(list.querySelectorAll<HTMLElement>(".combo__option"));

    // Server-rendered chips are already-chosen: take their name and drop them from the list.
    const first = this.chips.querySelector<HTMLInputElement>("input[type=hidden]");
    if (first?.name) this.fieldName = first.name;
    this.syncOptions();

    // Opens on typing (or ArrowDown), NOT on focus. The list floats over whatever sits
    // below the field — including the form's own Guardar button — so leaving it open just
    // because the box has focus turns a click on "Guardar" into picking a material.
    this.input.addEventListener("input", () => this.open());
    this.input.addEventListener("keydown", (e) => this.onKey(e));

    // Mousedown, not click: the option must register before the input loses focus.
    this.list.addEventListener("mousedown", (e) => {
      const opt = (e.target as HTMLElement).closest<HTMLElement>(".combo__option");
      if (!opt || opt.hidden) return;
      e.preventDefault();
      this.add(opt);
    });

    this.chips.addEventListener("click", (e) => {
      const btn = (e.target as HTMLElement).closest<HTMLElement>("[data-chip-remove]");
      if (!btn) return;
      const chip = btn.closest<HTMLElement>("[data-chip]");
      if (!chip) return;
      chip.remove();
      this.syncOptions();
    });

    this.input.addEventListener("blur", () => window.setTimeout(() => this.close(), 120));
  }

  /** The ids currently held as chips. */
  private chosen(): Set<string> {
    return new Set(
      Array.from(this.chips.querySelectorAll<HTMLElement>("[data-chip]")).map(
        (c) => c.dataset["chip"] ?? "",
      ),
    );
  }

  private add(opt: HTMLElement): void {
    const id = opt.dataset["value"] ?? "";
    if (id === "" || this.chosen().has(id)) return;

    const label = (opt.textContent ?? "").trim();
    const chip = document.createElement("span");
    chip.className = "chip";
    chip.dataset["chip"] = id;
    chip.textContent = label;

    const hidden = document.createElement("input");
    hidden.type = "hidden";
    hidden.name = this.fieldName;
    hidden.value = id;

    const remove = document.createElement("button");
    remove.type = "button"; // never submit the form
    remove.className = "chip__remove";
    remove.dataset["chipRemove"] = "";
    remove.setAttribute("aria-label", `Quitar ${label}`);
    remove.textContent = "✕";

    chip.append(hidden, remove);
    this.chips.append(chip);

    // Clear the query and close: the next pick starts by typing again, and the buttons
    // under the field stay clickable in between.
    this.input.value = "";
    this.close();
    this.input.focus();
  }

  /** Hides options that are already chips, then re-applies the search filter. */
  private syncOptions(): void {
    const taken = this.chosen();
    const q = this.input.value.trim().toLowerCase();
    let firstVisible = -1;
    this.options.forEach((opt, i) => {
      const id = opt.dataset["value"] ?? "";
      const hay = opt.dataset["search"] ?? (opt.textContent ?? "").toLowerCase();
      const show = !taken.has(id) && (q === "" || hay.includes(q));
      opt.hidden = !show;
      if (show && firstVisible === -1) firstVisible = i;
    });
    this.setActive(firstVisible);
  }

  private open(): void {
    this.syncOptions();
    this.list.hidden = false;
    this.input.setAttribute("aria-expanded", "true");
  }

  private close(): void {
    this.list.hidden = true;
    this.input.setAttribute("aria-expanded", "false");
    this.setActive(-1);
    this.input.value = "";
  }

  private onKey(e: KeyboardEvent): void {
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      if (this.list.hidden) this.open();
      this.move(e.key === "ArrowDown" ? 1 : -1);
    } else if (e.key === "Enter") {
      const active = this.options[this.activeIndex];
      if (!this.list.hidden && active && !active.hidden) {
        // Enter picks the highlighted option instead of submitting the form.
        e.preventDefault();
        this.add(active);
      }
    } else if (e.key === "Backspace" && this.input.value === "") {
      // Empty box + backspace drops the last chip, like a tag field.
      const last = this.chips.querySelector<HTMLElement>("[data-chip]:last-of-type");
      if (last) {
        last.remove();
        this.syncOptions();
      }
    } else if (e.key === "Escape") {
      this.close();
    }
  }

  private move(delta: number): void {
    const visible = this.options.filter((o) => !o.hidden);
    if (visible.length === 0) return;
    const curr = this.options[this.activeIndex];
    const at = curr && !curr.hidden ? visible.indexOf(curr) : -1;
    const next = visible[(at + delta + visible.length) % visible.length];
    if (next) this.setActive(this.options.indexOf(next));
  }

  private setActive(index: number): void {
    this.activeIndex = index;
    this.options.forEach((opt, i) => opt.classList.toggle("is-active", i === index));
    this.options[index]?.scrollIntoView({ block: "nearest" });
  }
}
